/**
 * La cortina que se pone al eliminar un proyecto.
 *
 * Hermana de `ProjectSwitchOverlay` —usa su escenografía y termina encadenando
 * SU MISMO relevo, importado de allí— y gemela de `ProjectRestoreOverlay`, que
 * hace lo contrario con la misma forma. Lo que pasa debajo es lo de siempre: el
 * panel entero va a cambiar de contenido y sin taparlo se recompondría a la
 * vista.
 *
 * ── El guion ────────────────────────────────────────────────────────────────
 *
 *   borrando   la mascota gira con la papelera; corre la petición
 *   hecho      «Eliminado» — un respiro para que el hecho se lea
 *   relevo     el nombre viejo se va y entra el del proyecto al que se llega
 *
 * Son tres estados y no uno porque el salto directo de la confirmación al panel
 * nuevo no cuenta lo que pasó: se vería desaparecer un proyecto y aparecer otro
 * sin que nada diga que lo primero fue un borrado y lo segundo su consecuencia.
 *
 * ── Qué hacen esos segundos ─────────────────────────────────────────────────
 *
 * Aquí hubo una cuenta atrás con botón de cancelar, y con ella la petición se
 * lanzaba al llegar a cero: los cinco segundos ERAN la ventana para
 * arrepentirse. Sin ese botón esa razón desaparece, así que la petición corre
 * desde el primer fotograma y lo que queda es un mínimo, para que la escena no
 * aparezca y desaparezca en un parpadeo cuando el servidor contesta rápido.
 *
 * Arrepentirse sigue siendo posible, sólo que en otro sitio y con más calma: el
 * borrado guarda treinta días y el proyecto aparece en el selector, apagado,
 * con su plazo y su botón de recuperar.
 */
import { useEffect, useRef, useState } from 'react'
import { IconTrash } from '@tabler/icons-react'
import { LoadingMascot } from '@webbuilder/ui'
import { HOLD_MS, RELAY_MS } from './ProjectSwitchOverlay'
import { translate, useI18n } from '../lib/i18n'

/** Lo que dura como mínimo el «Borrando…», para que se lea. */
const DELETE_MIN_MS = 5_000

/** Lo que se queda «Eliminado · puedes recuperarlo durante 30 días» antes del
 *  relevo. No es la pausa de 420 del cambio de proyecto: ahí sólo se reconoce
 *  un nombre, y aquí hay una frase que leer con un dato que importa —el plazo
 *  para arrepentirse—. A 420 desaparecía antes de terminar de leerla. */
const DONE_HOLD_MS = 2_400

type Phase = 'deleting' | 'done' | 'relay'

/** Un proyecto sin nombre sigue teniendo que ocupar el renglón. */
/* `translate` y no el hook: esto es una función suelta, no un componente, y un
   hook no puede vivir aquí. Ver `lib/i18n`. */
function label(name: string): string {
  return name.trim() || translate('deleteOverlay.noName')
}

export function ProjectDeleteOverlay({ projectName, nextProjectName, onDelete, onSwap, onDone, onFailed }: {
  projectName: string
  /**
   * A dónde se irá al terminar.
   *
   * Vacío cuando no queda ninguno: entonces se anuncia el asistente de creación
   * en vez de un nombre. Saberlo de antemano es lo que evita que el panel
   * aparezca detrás sin explicación.
   */
  nextProjectName: string
  /** Borra de verdad. Devuelve si salió bien. */
  onDelete: () => Promise<boolean>
  /** Cambiar el panel de abajo. Se llama UNA vez, a mitad del relevo. */
  onSwap: () => void
  /** Retirar la cortina. Quien la puso es quien la quita. */
  onDone: () => void
  /** La petición falló: no se ha borrado nada y hay que quitar la cortina. */
  onFailed: () => void
}) {
  const { t } = useI18n()
  const [phase, setPhase] = useState<Phase>('deleting')

  /* Los avisos en referencias y fuera de las dependencias: son funciones nuevas
     en cada render de quien nos monta, y con ellas en la lista el guion se
     reiniciaría en cada repintado del panel —que justo ahora está cargando
     cosas— y no llegaría nunca al final. Misma razón que en sus hermanas. */
  const callbacks = useRef({ onDelete, onSwap, onDone, onFailed })
  callbacks.current = { onDelete, onSwap, onDone, onFailed }

  useEffect(() => {
    if (phase !== 'deleting') return
    let alive = true
    /* La petición y el mínimo, a la vez y no en serie: si el servidor tarda más
       que el mínimo manda el servidor, y si tarda menos manda el mínimo. Se
       espera lo que dure el más lento de los dos, nunca la suma. */
    void Promise.all([
      callbacks.current.onDelete(),
      new Promise((resolve) => window.setTimeout(resolve, DELETE_MIN_MS)),
    ]).then(([ok]) => {
      if (!alive) return
      if (ok) setPhase('done')
      else callbacks.current.onFailed()
    })
    return () => { alive = false }
  }, [phase])

  useEffect(() => {
    if (phase !== 'done') return
    const toRelay = window.setTimeout(() => { setPhase('relay') }, DONE_HOLD_MS)
    return () => { window.clearTimeout(toRelay) }
  }, [phase])

  useEffect(() => {
    if (phase !== 'relay') return
    /* El cambio del panel ocurre al ARRANCAR el relevo, con la pantalla en su
       momento más ocupado: cualquier salto de maquetación al recomponerse pasa
       detrás de la cortina y con la vista puesta en el renglón. */
    callbacks.current.onSwap()
    const end = window.setTimeout(() => { callbacks.current.onDone() }, RELAY_MS + HOLD_MS)
    return () => { window.clearTimeout(end) }
  }, [phase])

  const arrived = phase === 'relay'

  return (
    <div
      className="dash-project-switch dash-project-delete"
      role="dialog"
      aria-modal="true"
      aria-label={t('deleteOverlay.dialogLabel', { name: label(projectName) })}
    >
      <p className="dash-sr-only" role="status" aria-live="polite">
        {phase === 'deleting' ? t('deleteOverlay.srDeleting')
          : phase === 'done' ? t('deleteOverlay.srDone')
            : nextProjectName ? t('deleteOverlay.srProject', { name: label(nextProjectName) }) : t('deleteOverlay.srNew')}
      </p>

      <div className="dash-project-switch-stage">
        <span className={`dash-project-switch-mascot${arrived ? ' is-relay' : ''}`}>
          <LoadingMascot spinning={phase !== 'done'} size={76} restWhenIdle />

          {/* La papelera colgada de la mascota, mientras dura el borrado.
              Es lo que distingue esta escena de las otras dos: las tres tienen la
              misma mascota girando, y sin un símbolo propio la de eliminar se
              leería igual que la de cambiar de proyecto hasta que apareciera el
              texto. Se va con el relevo, que ya es otra cosa. */}
          {!arrived ? (
            <span className="dash-project-delete-badge" aria-hidden="true">
              <IconTrash size={15} />
            </span>
          ) : null}
        </span>

        <div className="dash-project-switch-plate">
          {/* El mismo relevo de la cortina de cambio, con sus mismas clases: el
              nombre que se borra sube y se va, y entra el del proyecto al que se
              llega. */}
          {arrived ? (
            <span className="dash-project-switch-name is-out" aria-hidden="true">{label(projectName)}</span>
          ) : null}
          <span className={`dash-project-switch-name${arrived ? ' is-in' : ''}`}>
            {arrived
              ? (nextProjectName ? label(nextProjectName) : t('deleteOverlay.firstSite'))
              : label(projectName)}
          </span>
        </div>

        <p className={`dash-project-switch-role${arrived ? ' is-in' : ''}`}>
          {phase === 'deleting' ? t('deleteOverlay.deleting')
            : phase === 'done' ? t('deleteOverlay.done')
              : nextProjectName ? t('deleteOverlay.switching') : t('deleteOverlay.fromScratch')}
        </p>
      </div>
    </div>
  )
}
